import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import '../../style/scss/import.scss';

// Describes the types of legacy migration emails
export type MigrationEmailType = 'announcement' | 'golive' | 'handover';

// Describes the props of the migration mail preview modal
interface MigrationMailPreviewModalProps {
    legacyEnvObjectId: number;
    emailType: MigrationEmailType;
    subject: string;
    bodyHtml: string;
    recipients: string[] | null;
    alreadySent: boolean;
    onSent: (legacyEnvObjectId: number, emailType: MigrationEmailType) => void;
}

const MigrationMailPreviewModal: React.FC<MigrationMailPreviewModalProps> = ({
    legacyEnvObjectId, emailType, subject, bodyHtml, recipients, alreadySent, onSent
}) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();
    const [buttonLoader, setButtonLoader] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Reveal the modal after the initialization
    useEffect(() => {
        revealModal();
    }, []);

    // Send the migration email to the main user
    const sendMigrationMail = async () => {
        setButtonLoader(true);
        setErrorMessage(null);
        try {
            await saveData({
                apiUrl: 'send_legacy_migration_email',
                method: 'post',
                data: {
                    legacy_environment_id: legacyEnvObjectId,
                    email_type: emailType,
                    force: alreadySent,
                }
            });

            // Update the wizard state and close the preview modal
            onSent(legacyEnvObjectId, emailType);
            closeModal();
        } catch (err: any) {
            console.error("Error sending migration mail", err);

            // Show the error detail of the backend
            setErrorMessage(err?.response?.data?.detail || 'legacy_migration.general.mail_send_failed_message');
        } finally {
            setButtonLoader(false);
        }
    };

    return (
        <div className='migration-mail-preview-modal'>
            <p className='migration-mail-preview-meta'>
                <strong>{t('legacy_migration.general.mail_recipient_label')}:</strong> {(recipients || []).join(', ')}<br />
                <strong>{t('legacy_migration.general.mail_subject_label')}:</strong> {subject}
            </p>
            <iframe
                className='migration-mail-preview-body'
                title='migration-mail-preview'
                srcDoc={bodyHtml}
            />
            {errorMessage && (
                <p className='repair-message'>{t(errorMessage)}</p>
            )}
            <div className='migration-mail-preview-modal-buttons'>
                <SecondaryButton
                    onClick={() => closeModal()}
                    label='general.close'
                    size='small'
                />
                <PrimaryButton
                    onClick={() => sendMigrationMail()}
                    label={alreadySent
                        ? 'legacy_migration.general.mail_send_again_button_label'
                        : 'legacy_migration.general.mail_send_button_label'}
                    size='small'
                    loading={buttonLoader}
                />
            </div>
        </div>
    );
};

export default MigrationMailPreviewModal;
